import { Controller } from "@hotwired/stimulus"
import {
    scrollPanPositionValue,
    scrollPanRebasedStartTop,
    scrollPanStartTop
} from "../lib/large_image_scroll_pan.js"

const LARGE_IMAGE_CARD_SELECTOR = '.nfr-news-article--featured .nfr-article-card, .nfr-news-article--prominent .nfr-article-card, .nfr-news-article--image-overlay .nfr-article-card, .nfr-story-card--lead .nfr-story-card-surface, .nfr-story-card--overlay .nfr-story-card-surface'
const LARGE_IMAGE_SELECTOR = '.nfr-featured-image, .nfr-article-image, .nfr-article-media-image, .nfr-background-video, .nfr-story-card-image'
const BACKGROUND_VIDEO_SELECTOR = 'video[data-nfr-background-video]'
const SCROLL_PAN_PROPERTY = '--nfr-image-position-y'
const EXPAND_TOGGLE_SELECTOR = '.nfr-card-expand-toggle'
// The line-clamped card text (reader_app.scss); expanding a card only lifts these clamps.
const CLAMPED_CARD_TEXT_SELECTOR = '.nfr-article-title a, .nfr-article-summary, .nfr-article-summary-no-image'

// A clamp that cuts text hides at least one whole line. Tight line-heights let heavy glyphs
// overflow a fitting clamp by a few pixels, so anything under half a line is not clipped.
const clampedTextIsClipped = (element) => {
    const style = window.getComputedStyle(element)
    const lineHeight = Number.parseFloat(style.lineHeight) || Number.parseFloat(style.fontSize) * 1.2 || 0
    return element.scrollHeight - element.clientHeight > Math.max(lineHeight / 2, 1)
}

const backgroundVideoMotionIsReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

const updateBackgroundVideoPlayback = (video) => {
    const shouldPlay = video.dataset.nfrVideoVisible === 'true' &&
        document.visibilityState !== 'hidden' &&
        !backgroundVideoMotionIsReduced()

    if (!shouldPlay) {
        video.pause()
        if (backgroundVideoMotionIsReduced()) video.classList.remove('is-playing')
        return
    }

    if (!video.paused) return

    video.muted = true
    const playRequest = video.play()
    if (playRequest?.catch) {
        playRequest.catch(() => video.classList.remove('is-playing'))
    }
}

const updateBackgroundVideoCollection = () => {
    document.querySelectorAll(BACKGROUND_VIDEO_SELECTOR).forEach(updateBackgroundVideoPlayback)
}

// Connects to data-controller="feeds"
export default class extends Controller {
    static targets = [
        "items",
        "content",
        "results",
        "feedForm",
        "globalSearchInput",
        "categoryTitle",
        "feedSelectionRow",
        "localSearchInput",
        "localSearchPanel",
        "localSearchToggle",
        "selectedFeedSummary",
        "selectedFeedCount",
        "selectedFeedChips",
        "selectedFeedEmpty",
        "feedPickerBackdrop",
        "feedPickerSheet",
        "feedPickerToggle",
        "feedPickerRow",
        "feedPickerFilter",
        "feedPickerList",
        "feedPickerResults",
        "feedPickerStatus",
        "feedPickerSourceCount",
        "sourceRemovalDialog",
        "sourceRemovalTitle",
        "sourceRemovalMessage",
        "sourceRemovalError",
        "sourceRemovalViewButton",
        "sourceRemovalAccountButton",
        "sourceRemovalCancelButton",
        "undoToast",
        "undoToastMessage",
        "undoToastButton"
    ]
    static values = {
        resetUrl: String,
        // All-sources model (R-A5): when set, the picker searches this JSON endpoint instead of
        // filtering rows rendered on the page, so the reader never renders every catalog row.
        pickerSearchUrl: String,
        // All-sources model: DELETE here (with the source id in place of __SOURCE_ID__) unhides a
        // source, which is the Undo for a hide from a story's source menu.
        unhideUrlTemplate: String
    }

    connect() {
        this.globalSearchModeActive = false
        this.localSearchVisible = this.hasLocalSearchPanelTarget ? !this.localSearchPanelTarget.hidden : false
        this.localSearchAnimationEnabled = false
        this.localSearchAnimationTimeout = null
        this.pendingFeedSignature = null
        this.pendingSourceRemoval = null
        this.sourceRemovalBusy = false
        this.lastAutoSubmitSignature = null
        this.loadingTimeout = null
        this.feedPickerSearchTimeout = null
        this.feedPickerSearchController = null
        this.feedPickerLoaded = false
        this.expandToggleObserver = null
        this.expandToggleObservedText = new WeakSet()
        this.boundRefreshReaderContent = () => this.refreshReaderContent()
        this.boundFrameLoad = () => {
            this.refreshReaderContent()
            this.requestInitialSelectedFeeds()
        }
        this.boundFeedPickerKeydown = (event) => this.closeFeedPickerOnEscape(event)
        this.boundFontsLoaded = () => this.updateArticleExpandToggles(this.expandableArticles())

        this.initializeToasts()
        this.setupStreamHandlers()
        this.updateLocalSearchPanelVisibility()
        this.localSearchAnimationEnabled = true
        this.refreshReaderContent()
        this.requestInitialSelectedFeeds()
        
        document.addEventListener('turbo:load', this.boundRefreshReaderContent)
        document.addEventListener('turbo:frame-load', this.boundFrameLoad)
        document.addEventListener('keydown', this.boundFeedPickerKeydown)
        // Late web fonts can rewrap a clamp without resizing it, so re-measure when they land.
        document.fonts?.addEventListener?.('loadingdone', this.boundFontsLoaded)
    }

    refreshReaderContent() {
        this.initializeSwiper()
        this.initializeArticleExpandToggles()
        this.initializeNewsCards()
        this.initializeBackgroundVideos()
        this.initializeParallaxImages()
        this.formatLocalTimes()
    }

    formatLocalTimes() {
        const timeElements = document.querySelectorAll('.local-time[data-timestamp]')
        timeElements.forEach(el => {
            const timestamp = el.dataset.timestamp
            if (timestamp && !el.dataset.formatted) {
                try {
                    const date = new Date(timestamp)
                    if (!isNaN(date.getTime())) {
                        // "2:30 PM" for section ranges; otherwise "Feb 2, 2026, 2:30 PM"
                        const formatted = el.dataset.timeFormat === 'clock'
                            ? date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
                            : date.toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                            })
                        el.textContent = formatted
                        el.dataset.formatted = 'true'
                    }
                } catch (e) {
                    console.warn('Failed to format timestamp:', timestamp, e)
                }
            }
        })

        // Relative story times ("42 min ago") keep the exact local time in their tooltip.
        document.querySelectorAll('.nfr-story-time[datetime]:not([data-titled])').forEach((el) => {
            const date = new Date(el.getAttribute('datetime'))
            if (isNaN(date.getTime())) return

            el.title = date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
            el.dataset.titled = 'true'
        })
    }

    initializeSwiper() {
        if (!window.Swiper) return

        const containers = document.querySelectorAll('.swiper-theme-container')
        
        containers.forEach(container => {
            const mainSwiper = container.querySelector('.theme-slider')
            if (mainSwiper) {
                if (mainSwiper.swiper) {
                    mainSwiper.swiper.update()
                    return
                }

                const nextButton = container.querySelector('.swiper-button-next')
                const prevButton = container.querySelector('.swiper-button-prev')
                const paginationElement = container.querySelector('.nfr-featured-pagination')
                const swiperOptions = {
                    spaceBetween: 8,
                    loop: true,
                    grabCursor: true,
                    autoplay: {
                        delay: 5000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                    }
                }

                if (nextButton && prevButton) {
                    swiperOptions.navigation = {
                        nextEl: nextButton,
                        prevEl: prevButton
                    }
                }

                if (paginationElement) {
                    swiperOptions.pagination = {
                        el: paginationElement,
                        clickable: true
                    }
                }
                
                // Initialize main swiper with options from data attribute merged with default options
                const dataOptions = mainSwiper.dataset.swiper ? JSON.parse(mainSwiper.dataset.swiper) : {}
                const thumbSwiper = container.querySelector('.thumb')
                const thumbInstance = thumbSwiper ? new Swiper(thumbSwiper, {
                    spaceBetween: 8,
                    slidesPerView: 5,
                    freeMode: false,
                    watchSlidesProgress: true,
                    watchSlidesVisibility: true,
                    grabCursor: true,
                    slideToClickedSlide: true,
                    touchRatio: 1,
                    preventClicks: false,
                    preventClicksPropagation: false
                }) : null

                const mainInstance = new Swiper(mainSwiper, { 
                    ...swiperOptions, 
                    ...dataOptions,
                    thumbs: thumbInstance ? {
                        swiper: thumbInstance,
                        slideThumbActiveClass: 'swiper-slide-thumb-active',
                        thumbsContainerClass: 'swiper-thumbs'
                    } : undefined,
                    on: {
                        slideChange: function() {
                            if (prevButton) {
                                if (this.isBeginning) {
                                    prevButton.style.display = 'none';
                                } else {
                                    prevButton.style.display = 'flex';
                                }
                            }

                            if (nextButton) {
                                if (this.isEnd) {
                                    nextButton.style.display = 'none';
                                } else {
                                    nextButton.style.display = 'flex';
                                }
                            }
                        },
                        init: function() {
                            // Start autoplay when swiper is initialized
                            if (this.params.autoplay && this.params.autoplay.delay) {
                                this.autoplay.start();
                            }
                            
                            // Highlight the initial active thumbnail
                            if (thumbInstance) {
                                const activeIndex = this.activeIndex;
                                const thumbSlides = thumbSwiper.querySelectorAll('.swiper-slide');
                                thumbSlides.forEach((slide, index) => {
                                    if (index === activeIndex) {
                                        slide.classList.add('swiper-slide-thumb-active');
                                    } else {
                                        slide.classList.remove('swiper-slide-thumb-active');
                                    }
                                });
                            }
                        },
                        slideChangeTransitionEnd: function() {
                            // Update the active thumbnail when slide changes
                            if (thumbInstance) {
                                const activeIndex = this.activeIndex;
                                const realIndex = this.realIndex; // For looped sliders
                                const thumbSlides = thumbSwiper.querySelectorAll('.swiper-slide');
                                
                                thumbSlides.forEach((slide, index) => {
                                    if (this.params.loop) {
                                        // For looped sliders, use realIndex
                                        if (index === realIndex) {
                                            slide.classList.add('swiper-slide-thumb-active');
                                        } else {
                                            slide.classList.remove('swiper-slide-thumb-active');
                                        }
                                    } else {
                                        // For non-looped sliders
                                        if (index === activeIndex) {
                                            slide.classList.add('swiper-slide-thumb-active');
                                        } else {
                                            slide.classList.remove('swiper-slide-thumb-active');
                                        }
                                    }
                                });
                                
                                // Scroll the thumbnail into view if needed
                                const activeThumb = thumbSwiper.querySelector('.swiper-slide-thumb-active');
                                if (activeThumb) {
                                    thumbInstance.slideTo(realIndex);
                                }
                            }
                        }
                    }
                })
            }
        })
    }

    filterBySource(event) {
        event.preventDefault()

        const sourceChip = event.currentTarget
        const selectedSource = (sourceChip.dataset.readerSourceFilter || '').toLowerCase()
        const sourceTabs = sourceChip.closest('.nfr-source-tabs')
        if (sourceTabs) {
            sourceTabs.querySelectorAll('[data-reader-source-filter]').forEach((chip) => {
                chip.classList.toggle('active', chip === sourceChip)
                chip.setAttribute('aria-pressed', chip === sourceChip ? 'true' : 'false')
            })
        }

        this.articleElements().forEach((article) => {
            const articleSource = (article.dataset.feedTitle || '').toLowerCase()
            const matches = selectedSource === '' || articleSource === selectedSource
            article.classList.toggle('nfr-hidden', !matches)
            article.style.display = matches ? '' : 'none'
        })

        this.updateArticleRowsVisibility()
    }

    focusGlobalSearch(event) {
        event.preventDefault()

        if (this.isMobileReaderViewport() && this.hasLocalSearchPanelTarget && this.hasLocalSearchInputTarget) {
            this.setLocalSearchVisible(true)
            this.localSearchPanelTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
            this.localSearchInputTarget.focus()
            return
        }

        if (this.hasGlobalSearchInputTarget) {
            this.globalSearchInputTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
            this.globalSearchInputTarget.focus()
        }
    }

    isMobileReaderViewport() {
        return window.matchMedia?.('(max-width: 767.98px)').matches === true
    }

    scrollToTop(event) {
        event.preventDefault()
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    searchLocalArticles(event) {
        this.applyLocalArticleSearch(event.currentTarget.value)
    }

    toggleLocalSearch(event) {
        event.preventDefault()

        const nextVisible = this.hasLocalSearchPanelTarget ? this.localSearchPanelTarget.hidden : !this.localSearchVisible
        this.setLocalSearchVisible(nextVisible)

        if (nextVisible && this.hasLocalSearchInputTarget) {
            this.localSearchInputTarget.focus()
        }
    }

    openFeedPicker(event) {
        event.preventDefault()
        if (!this.hasFeedPickerSheetTarget) return

        window.clearTimeout(this.feedPickerCloseTimeout)
        this.feedPickerSheetTarget.hidden = false
        if (this.hasFeedPickerBackdropTarget) this.feedPickerBackdropTarget.hidden = false
        document.body.classList.add('nfr-reader-sheet-open')

        requestAnimationFrame(() => {
            this.feedPickerSheetTarget.classList.add('is-open')
            if (this.hasFeedPickerBackdropTarget) this.feedPickerBackdropTarget.classList.add('is-open')
            this.feedPickerToggleTargets.forEach((button) => button.setAttribute('aria-expanded', 'true'))
            this.feedPickerSheetTarget.focus()
        })

        if (this.hasPickerSearchUrlValue && !this.feedPickerLoaded) {
            this.feedPickerLoaded = true
            this.searchFeedPicker(this.hasFeedPickerFilterTarget ? this.feedPickerFilterTarget.value : '')
        }
    }

    closeFeedPicker(event) {
        if (event && event.currentTarget?.type !== 'submit') {
            event.preventDefault()
        }

        if (!this.hasFeedPickerSheetTarget || this.feedPickerSheetTarget.hidden) return

        this.feedPickerSheetTarget.classList.remove('is-open')
        if (this.hasFeedPickerBackdropTarget) this.feedPickerBackdropTarget.classList.remove('is-open')
        this.feedPickerToggleTargets.forEach((button) => button.setAttribute('aria-expanded', 'false'))
        document.body.classList.remove('nfr-reader-sheet-open')

        window.clearTimeout(this.feedPickerCloseTimeout)
        this.feedPickerCloseTimeout = window.setTimeout(() => {
            if (this.feedPickerSheetTarget.classList.contains('is-open')) return

            this.feedPickerSheetTarget.hidden = true
            if (this.hasFeedPickerBackdropTarget) this.feedPickerBackdropTarget.hidden = true
        }, 220)
    }

    closeFeedPickerOnEscape(event) {
        if (event.key !== 'Escape') return
        if (!this.hasFeedPickerSheetTarget || this.feedPickerSheetTarget.hidden) return

        this.closeFeedPicker(event)
        this.feedPickerToggleTargets[0]?.focus()
    }

    toggleArticleExpansion(event) {
        event.preventDefault()
        event.stopPropagation()

        const button = event.currentTarget
        const article = button.closest('.nfr-news-article')
        if (!article) return

        const expanded = !article.classList.contains('is-expanded')
        this.transitionArticleExpansion(article, button, expanded)
    }

    transitionArticleExpansion(article, button, expanded) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const mutate = () => this.setArticleExpansionState(article, button, expanded)

        if (!prefersReducedMotion && document.startViewTransition && article.dataset.nfrArticleTransitioning !== 'true') {
            const transitionName = `nfr-article-${button.getAttribute('aria-controls') || Date.now()}`
            article.style.viewTransitionName = transitionName
            article.style.viewTransitionClass = 'nfr-article-expansion'
            article.dataset.nfrArticleTransitioning = 'true'

            const clearViewTransitionState = () => {
                article.style.removeProperty('view-transition-name')
                article.style.removeProperty('view-transition-class')
                delete article.dataset.nfrArticleTransitioning
                this.updateParallaxImages()
            }

            try {
                const transition = document.startViewTransition(mutate)
                transition.finished.finally(clearViewTransitionState)
            } catch (_error) {
                clearViewTransitionState()
                this.animateArticleHeight(article, mutate)
            }

            return
        }

        this.animateArticleHeight(article, mutate)
    }

    setArticleExpansionState(article, button, expanded) {
        article.classList.toggle('is-expanded', expanded)
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false')

        const label = expanded ? button.dataset.expandedLabel : button.dataset.collapsedLabel
        if (label) {
            button.setAttribute('aria-label', label)
            button.setAttribute('title', label)
        }

        const slider = article.closest('.theme-slider')
        if (slider?.swiper) {
            slider.swiper.update()
        }

        // Collapsing may not resize the clamps (the text can fit both ways after a resize).
        this.updateArticleExpandToggles([article])
    }

    // The expand chevron is server-rendered visible; once measured, it shows only on cards whose
    // clamped title or summary is cut off, plus expanded cards (so they can collapse) and a
    // focused chevron (so keyboard focus is never dropped).
    initializeArticleExpandToggles() {
        if (!this.expandToggleObserver && 'ResizeObserver' in window) {
            // Card width follows the grid and the viewport, so a clamp can start or stop clipping.
            this.expandToggleObserver = new ResizeObserver((entries) => {
                const articles = new Set()
                entries.forEach(({ target }) => {
                    if (!target.isConnected) {
                        this.expandToggleObserver?.unobserve(target)
                        return
                    }

                    const article = target.closest('.nfr-news-article')
                    if (article) articles.add(article)
                })
                this.updateArticleExpandToggles([...articles])
            })
        }

        const articles = this.expandableArticles()
        articles.forEach((article) => {
            article.querySelectorAll(CLAMPED_CARD_TEXT_SELECTOR).forEach((element) => {
                if (this.expandToggleObservedText.has(element)) return

                this.expandToggleObservedText.add(element)
                this.expandToggleObserver?.observe(element)
            })
        })
        this.updateArticleExpandToggles(articles)
    }

    expandableArticles() {
        return Array.from(this.element.querySelectorAll('.nfr-news-article'))
            .filter((article) => article.querySelector(EXPAND_TOGGLE_SELECTOR) &&
                article.closest('[data-controller~="feeds"]') === this.element)
    }

    updateArticleExpandToggles(articles) {
        // Measure every card before toggling any button so one pass costs one layout.
        const updates = articles.flatMap((article) => {
            const button = article.querySelector(EXPAND_TOGGLE_SELECTOR)
            // Skip cards that are filtered out or not laid out; their clamps report no overflow.
            if (!button || !article.getClientRects().length) return []

            const visible = article.classList.contains('is-expanded') ||
                button === document.activeElement ||
                Array.from(article.querySelectorAll(CLAMPED_CARD_TEXT_SELECTOR)).some(clampedTextIsClipped)
            return [[button, visible]]
        })

        updates.forEach(([button, visible]) => {
            if (button.hidden === visible) button.hidden = !visible
        })
    }

    animateArticleHeight(article, mutate) {
        const startHeight = article.getBoundingClientRect().height

        article.style.height = `${startHeight}px`
        article.style.overflow = 'hidden'
        article.style.transition = 'none'
        article.getBoundingClientRect()

        mutate()

        const endHeight = article.scrollHeight

        if (Math.abs(endHeight - startHeight) < 1) {
            this.clearArticleHeightAnimation(article)
            this.updateParallaxImages()
            return
        }

        requestAnimationFrame(() => {
            article.style.transition = 'height 260ms cubic-bezier(0.22, 1, 0.36, 1)'
            article.style.height = `${endHeight}px`
        })

        article.addEventListener('transitionend', (event) => {
            if (event.propertyName !== 'height') return

            this.clearArticleHeightAnimation(article)
            this.updateParallaxImages()
        }, { once: true })
    }

    clearArticleHeightAnimation(article) {
        article.style.removeProperty('height')
        article.style.removeProperty('overflow')
        article.style.removeProperty('transition')
    }

    setLocalSearchVisible(visible) {
        this.localSearchVisible = visible

        if (!visible && this.hasLocalSearchInputTarget && this.localSearchInputTarget.value) {
            this.localSearchInputTarget.value = ''
            this.applyLocalArticleSearch('')
        }

        this.updateLocalSearchPanelVisibility()
    }

    updateLocalSearchPanelVisibility() {
        const visible = this.localSearchPanelShouldBeVisible()

        if (this.hasLocalSearchPanelTarget) {
            this.renderLocalSearchPanelVisibility(visible)
        }

        this.localSearchToggleTargets.forEach((button) => {
            button.setAttribute('aria-expanded', visible ? 'true' : 'false')
            button.classList.toggle('is-active', visible)
        })
    }

    localSearchPanelShouldBeVisible() {
        return Boolean(this.localSearchVisible && !this.globalSearchModeActive)
    }

    renderLocalSearchPanelVisibility(visible) {
        const panel = this.localSearchPanelTarget

        window.clearTimeout(this.localSearchAnimationTimeout)
        panel.classList.remove('is-entering', 'is-leaving')

        if (visible) {
            panel.hidden = false

            if (this.localSearchAnimationEnabled) {
                panel.classList.add('is-entering')
                panel.addEventListener('animationend', () => {
                    panel.classList.remove('is-entering')
                }, { once: true })
            }

            return
        }

        if (panel.hidden || !this.localSearchAnimationEnabled) {
            panel.hidden = true
            return
        }

        panel.classList.add('is-leaving')

        const hidePanel = () => {
            window.clearTimeout(this.localSearchAnimationTimeout)
            panel.classList.remove('is-leaving')
            panel.hidden = !this.localSearchPanelShouldBeVisible()
        }

        panel.addEventListener('animationend', hidePanel, { once: true })
        this.localSearchAnimationTimeout = window.setTimeout(hidePanel, 260)
    }

    globalSearchChanged(event) {
        if (event.currentTarget.value.trim().length > 0) return
        if (!this.globalSearchModeIsActive()) return

        this.resetGlobalSearchView()
    }

    startGlobalSearch() {
        const query = this.hasGlobalSearchInputTarget ? this.globalSearchInputTarget.value.trim() : ''
        if (!query) {
            this.resetGlobalSearchView()
            return
        }

        this.showGlobalSearchLoadingState(query)
    }

    startFeedSubmit() {
        if (!this.hasFeedFormTarget) return

        this.pendingFeedSignature = this.selectedFeedSignature(this.feedFormTarget)
        this.showLoadingState()
    }

    finishFeedSubmit(event) {
        window.clearTimeout(this.loadingTimeout)
        this.pendingFeedSignature = null

        if (event.detail.success === false && this.resultsElement()?.querySelector('.nfr-reader-loading')) {
            this.resultsElement().innerHTML = this.unableToLoadHtml()
        }
    }

    submitFeedForm() {
        if (!this.hasFeedFormTarget) return

        this.pendingFeedSignature = this.selectedFeedSignature(this.feedFormTarget)
        this.lastAutoSubmitSignature = this.pendingFeedSignature
        this.showLoadingState()
    }

    feedCheckboxChanged(event) {
        if (!this.hasFeedFormTarget) return

        const checkbox = event.currentTarget
        if (!checkbox.checked) {
            checkbox.checked = true
            this.updateSelectedFeedSummary(this.feedFormTarget)
            this.openSourceRemoval({ checkbox, trigger: checkbox })
            return
        }

        this.updateSelectedFeedSummary(this.feedFormTarget)
        this.requestSelectedFeeds(this.feedFormTarget)
    }

    filterFeedPicker(event) {
        const query = event.currentTarget.value.toLowerCase().trim()

        if (this.hasPickerSearchUrlValue) {
            window.clearTimeout(this.feedPickerSearchTimeout)
            this.feedPickerSearchTimeout = window.setTimeout(() => this.searchFeedPicker(query), 250)
            return
        }

        this.feedPickerRowTargets.forEach((row) => {
            row.hidden = query.length > 0 && !(row.dataset.feedName || '').includes(query)
        })
    }

    async searchFeedPicker(query) {
        if (!this.hasPickerSearchUrlValue || !this.hasFeedPickerResultsTarget) return

        this.feedPickerSearchController?.abort()
        this.feedPickerSearchController = new AbortController()
        this.setFeedPickerStatus('Searching sources…')

        const url = new URL(this.pickerSearchUrlValue, window.location.origin)
        if (query) url.searchParams.set('q', query)

        try {
            const response = await fetch(url, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
                signal: this.feedPickerSearchController.signal
            })
            if (!response.ok) throw new Error(`Picker search failed with ${response.status}`)

            const data = await response.json()
            this.renderFeedPickerResults(Array.isArray(data.sources) ? data.sources : [], data)
        } catch (error) {
            if (error.name === 'AbortError') return

            this.setFeedPickerStatus('Sources could not be loaded. Try again.')
        }
    }

    renderFeedPickerResults(sources, data = {}) {
        if (!this.hasFeedPickerResultsTarget) return

        // Keep anything the reader ticked from an earlier search: move it into the selected list.
        this.feedPickerResultsTarget.querySelectorAll('[data-feeds-target="feedPickerRow"]').forEach((row) => {
            const checkbox = row.querySelector('.reader-feed-checkbox')
            if (checkbox?.checked && this.hasFeedPickerListTarget) {
                this.feedPickerListTarget.append(row)
            } else {
                row.remove()
            }
        })

        const shown = sources.filter((source) => !this.feedFormTarget.querySelector(`.reader-feed-checkbox[value="${CSS.escape(source.id)}"]`))
        shown.forEach((source) => this.feedPickerResultsTarget.append(this.buildFeedPickerRow(source)))

        const total = Number(data.total || 0)
        const limit = Number(data.limit || 0)
        if (shown.length === 0) {
            this.setFeedPickerStatus(sources.length === 0 ? 'No matching sources.' : 'Every matching source is already selected.')
        } else if (limit && sources.length >= limit) {
            this.setFeedPickerStatus(`Showing the first ${shown.length} of ${total} sources. Keep typing to narrow the list.`)
        } else {
            this.setFeedPickerStatus(`${shown.length} ${shown.length === 1 ? 'source' : 'sources'} found.`)
        }
    }

    buildFeedPickerRow(source) {
        const row = document.createElement('label')
        row.className = 'nfr-feed-picker-row'
        row.dataset.feedsTarget = 'feedPickerRow'
        row.dataset.feedName = (source.name || '').toLowerCase()
        row.dataset.feedId = source.id

        const icon = document.createElement('span')
        icon.className = 'nfr-feed-picker-icon'
        icon.setAttribute('aria-hidden', 'true')
        icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 11a9 9 0 0 1 9 9"></path><path d="M4 4a16 16 0 0 1 16 16"></path><circle cx="5" cy="19" r="1"></circle></svg>'

        const copy = document.createElement('span')
        copy.className = 'nfr-feed-picker-copy'
        const name = document.createElement('strong')
        name.textContent = source.name || ''
        copy.append(name)
        const categories = Array.isArray(source.categories) ? source.categories.filter(Boolean) : []
        if (categories.length > 0) {
            const small = document.createElement('small')
            small.textContent = categories.map((category) => category.charAt(0).toUpperCase() + category.slice(1)).join(', ')
            copy.append(small)
        }

        const checkbox = document.createElement('input')
        checkbox.type = 'checkbox'
        checkbox.name = 'selected_feed_ids[]'
        checkbox.value = source.id
        checkbox.className = 'reader-feed-checkbox'
        checkbox.dataset.feedName = source.name || ''
        if (source.delete_url) checkbox.dataset.feedDeleteUrl = source.delete_url
        checkbox.dataset.action = 'change->feeds#feedCheckboxChanged'

        row.append(icon, copy, checkbox)
        return row
    }

    setFeedPickerStatus(text) {
        if (this.hasFeedPickerStatusTarget) this.feedPickerStatusTarget.textContent = text
    }

    removeFeedChip(event) {
        const removeButton = event.target.closest('[data-feed-chip-remove]')
        if (!removeButton || !this.hasFeedFormTarget) return

        event.preventDefault()
        const checkbox = this.feedFormTarget.querySelector(`.reader-feed-checkbox[value="${removeButton.dataset.feedChipRemove}"]`)
        if (checkbox) {
            this.openSourceRemoval({ checkbox, trigger: removeButton })
        }
    }

    openSourceRemoval({ checkbox, trigger }) {
        if (!checkbox || !this.hasFeedFormTarget) return

        const pendingRemoval = {
            checkbox,
            deleteUrl: checkbox.dataset.feedDeleteUrl || trigger?.dataset.feedDeleteUrl || '',
            feedId: checkbox.value,
            feedName: checkbox.dataset.feedName || trigger?.dataset.feedName || 'this source',
            trigger
        }

        if (!this.hasSourceRemovalDialogTarget) {
            this.commitViewRemoval(pendingRemoval)
            return
        }

        this.pendingSourceRemoval = pendingRemoval
        this.sourceRemovalBusy = false
        this.sourceRemovalTitleTarget.textContent = `Remove “${pendingRemoval.feedName}”?`
        this.sourceRemovalMessageTarget.textContent = pendingRemoval.deleteUrl
            ? 'Remove it only from this reader view, or choose “Don’t show again” to remove it from your sources. You can add catalog sources again later.'
            : 'Remove it only from this view. To keep it out of your source list across sessions, create a free account.'
        this.sourceRemovalErrorTarget.hidden = true
        this.sourceRemovalErrorTarget.textContent = ''
        this.setSourceRemovalBusy(false)
        this.sourceRemovalDialogTarget.showModal()
    }

    removeSourceFromView(event) {
        event.preventDefault()
        if (this.sourceRemovalBusy || !this.pendingSourceRemoval) return

        const pendingRemoval = this.pendingSourceRemoval
        this.sourceRemovalDialogTarget.close()
        this.commitViewRemoval(pendingRemoval)
    }

    async removeSourceFromAccount(event) {
        event.preventDefault()
        const pendingRemoval = this.pendingSourceRemoval
        if (this.sourceRemovalBusy || !pendingRemoval?.deleteUrl) return

        this.setSourceRemovalBusy(true)
        this.sourceRemovalErrorTarget.hidden = true

        try {
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
            const response = await fetch(pendingRemoval.deleteUrl, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
                }
            })

            if (!response.ok) throw new Error(`Feed removal failed with ${response.status}`)

            pendingRemoval.checkbox.checked = false
            pendingRemoval.checkbox.closest('[data-feeds-target="feedPickerRow"]')?.remove()
            this.updateFeedPickerSourceCount()
            this.updateSelectedFeedSummary(this.feedFormTarget)
            this.setSourceRemovalBusy(false)
            this.sourceRemovalDialogTarget.close()
            this.requestSelectedFeeds(this.feedFormTarget)
        } catch (_error) {
            this.setSourceRemovalBusy(false)
            this.sourceRemovalErrorTarget.textContent = 'Unable to remove this source from your sources. Please try again.'
            this.sourceRemovalErrorTarget.hidden = false
        }
    }

    cancelSourceRemoval(event) {
        event?.preventDefault()
        if (this.sourceRemovalBusy || !this.hasSourceRemovalDialogTarget) return

        this.sourceRemovalDialogTarget.close()
    }

    sourceRemovalDialogClosed() {
        const trigger = this.pendingSourceRemoval?.trigger
        this.pendingSourceRemoval = null
        this.sourceRemovalBusy = false
        trigger?.focus()
    }

    commitViewRemoval(pendingRemoval) {
        pendingRemoval.checkbox.checked = false
        this.updateSelectedFeedSummary(this.feedFormTarget)
        this.requestSelectedFeeds(this.feedFormTarget)
    }

    setSourceRemovalBusy(busy) {
        this.sourceRemovalBusy = busy
        this.sourceRemovalViewButtonTarget.disabled = busy
        this.sourceRemovalAccountButtonTarget.disabled = busy
        this.sourceRemovalCancelButtonTarget.disabled = busy
        this.sourceRemovalAccountButtonTarget.textContent = busy ? 'Removing…' : 'Don’t show again'
    }

    updateFeedPickerSourceCount() {
        if (!this.hasFeedPickerSourceCountTarget) return
        // On the all-sources model the count is the catalog's, not the rows on the page.
        if (this.hasPickerSearchUrlValue) return

        const count = this.feedFormTarget.querySelectorAll('.reader-feed-checkbox').length
        const noun = count === 1 ? 'source' : 'sources'
        const categoryLabel = this.feedPickerSourceCountTarget.dataset.categoryLabel || 'this category'
        this.feedPickerSourceCountTarget.textContent = `${count} ${noun} in ${categoryLabel}`
    }

    setGlobalSearchMode(active) {
        this.globalSearchModeActive = active

        this.feedSelectionRowTargets.forEach((element) => {
            element.hidden = active
        })

        this.updateLocalSearchPanelVisibility()

        this.selectedFeedSummaryTargets.forEach((element) => {
            element.hidden = active
        })

        if (this.hasCategoryTitleTarget) {
            if (!this.categoryTitleTarget.dataset.readerCategoryDefaultValue) {
                this.categoryTitleTarget.dataset.readerCategoryDefaultValue = this.categoryTitleTarget.textContent.trim()
            }
            this.categoryTitleTarget.textContent = active ? 'All feeds' : this.categoryTitleTarget.dataset.readerCategoryDefaultValue
        }
    }

    globalSearchModeIsActive() {
        return Boolean(
            this.globalSearchModeActive ||
            this.feedSelectionRowTargets.some((element) => element.hidden)
        )
    }

    resetGlobalSearchView() {
        const url = this.hasResetUrlValue ? this.resetUrlValue : '/feeds/feeds_stream?category=all&random=true'
        this.setGlobalSearchMode(false)
        this.showLoadingState()

        const frame = document.getElementById('feeds-stream-frame')
        if (frame) {
            frame.src = url
        } else if (window.Turbo?.visit) {
            window.Turbo.visit(url)
        } else {
            window.location.href = url
        }
    }

    showLoadingState() {
        this.setGlobalSearchMode(false)

        const results = this.resultsElement()
        if (results) {
            results.innerHTML = this.loadingHtml('Loading news items...')
        }

        window.clearTimeout(this.loadingTimeout)
        this.loadingTimeout = window.setTimeout(() => {
            const currentResults = this.resultsElement()
            if (!currentResults?.querySelector('.nfr-reader-loading')) return

            this.pendingFeedSignature = null
            currentResults.innerHTML = this.unableToLoadHtml()
        }, 45000)
    }

    showGlobalSearchLoadingState(query) {
        this.setGlobalSearchMode(true)

        const results = this.resultsElement()
        if (results) {
            const label = query ? `Searching "${query}"...` : 'Searching all my news...'
            results.innerHTML = this.loadingHtml(label)
        }
    }

    selectedFeedSignature(form) {
        return Array.from(form.querySelectorAll('.reader-feed-checkbox:checked'))
            .map((checkbox) => checkbox.value)
            .sort()
            .join(',')
    }

    updateSelectedFeedSummary(form) {
        const checkedBoxes = Array.from(form.querySelectorAll('.reader-feed-checkbox:checked'))

        this.selectedFeedCountTargets.forEach((counter) => {
            counter.textContent = checkedBoxes.length.toString()
        })

        if (!this.hasSelectedFeedChipsTarget) return

        this.selectedFeedChipsTarget.querySelectorAll('[data-feed-chip-remove]').forEach((chip) => chip.remove())

        checkedBoxes.forEach((checkbox) => {
            const chip = this.buildSelectedFeedChip(checkbox)
            this.selectedFeedChipsTarget.append(chip)
        })

        if (this.hasSelectedFeedEmptyTarget) {
            this.selectedFeedEmptyTarget.hidden = checkedBoxes.length > 0
        }
    }

    buildSelectedFeedChip(checkbox) {
        const chip = document.createElement('button')
        chip.type = 'button'
        chip.className = 'nfr-selected-feed-chip'
        chip.dataset.feedChipRemove = checkbox.value
        chip.dataset.feedName = checkbox.dataset.feedName || checkbox.value
        if (checkbox.dataset.feedDeleteUrl) chip.dataset.feedDeleteUrl = checkbox.dataset.feedDeleteUrl

        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
        icon.setAttribute('aria-hidden', 'true')
        icon.setAttribute('width', '18')
        icon.setAttribute('height', '18')
        icon.setAttribute('viewBox', '0 0 24 24')
        icon.setAttribute('fill', 'none')
        icon.setAttribute('stroke', 'currentColor')
        icon.setAttribute('stroke-width', '2.4')
        icon.setAttribute('stroke-linecap', 'round')
        icon.setAttribute('stroke-linejoin', 'round')
        const pathOne = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        pathOne.setAttribute('d', 'M18 6 6 18')
        const pathTwo = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        pathTwo.setAttribute('d', 'm6 6 12 12')
        icon.append(pathOne, pathTwo)

        const label = document.createElement('span')
        label.textContent = checkbox.dataset.feedName || checkbox.value
        chip.append(icon, label)

        return chip
    }

    requestSelectedFeeds(form) {
        const signature = this.selectedFeedSignature(form)
        if (this.pendingFeedSignature === signature) return

        this.pendingFeedSignature = signature
        this.lastAutoSubmitSignature = signature
        this.showLoadingState()
        form.requestSubmit()
    }

    requestInitialSelectedFeeds() {
        if (!this.hasFeedFormTarget) return

        const signature = this.selectedFeedSignature(this.feedFormTarget)
        const hasRenderedArticles = Boolean(this.resultsElement()?.querySelector('.nfr-news-card'))
        if (signature && (!hasRenderedArticles || this.lastAutoSubmitSignature !== signature) && this.pendingFeedSignature !== signature) {
            this.requestSelectedFeeds(this.feedFormTarget)
        }
    }

    loadingHtml(label) {
        const safeLabel = this.escapeHtml(label)
        return `<div class="nfr-reader-loading"><span class="nfr-reader-spinner" role="status" aria-hidden="true"></span><span>${safeLabel}</span></div>`
    }

    unableToLoadHtml() {
        return '<div class="nfr-reader-empty-state"><div class="nfr-reader-empty-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 11a9 9 0 0 1 9 9"></path><path d="M4 4a16 16 0 0 1 16 16"></path><circle cx="5" cy="19" r="1"></circle></svg></div><h2>Unable to load news</h2><p>Refresh the page or try a smaller feed selection.</p></div>'
    }

    resultsElement() {
        return this.hasResultsTarget ? this.resultsTarget : document.getElementById('feed-items-container')
    }

    articleElements() {
        return Array.from(this.resultsElement()?.querySelectorAll('.nfr-article-grid .nfr-article-column[data-feed-title]') || [])
    }

    updateArticleRowsVisibility() {
        document.querySelectorAll('#feed-items .nfr-article-row').forEach((row) => {
            const articles = Array.from(row.querySelectorAll('.nfr-article-column[data-feed-title]'))
            const hasVisibleArticle = articles.some((article) => {
                return article.style.display !== 'none' && !article.classList.contains('nfr-hidden')
            })

            row.hidden = articles.length > 0 && !hasVisibleArticle
        })

        // A time section whose every row is filtered out hides its header too.
        document.querySelectorAll('#feed-items .nfr-feed-section').forEach((section) => {
            const rows = Array.from(section.querySelectorAll('.nfr-article-row'))
            section.hidden = rows.length > 0 && rows.every((row) => row.hidden)
        })
    }

    // A story's source menu: the same filter as that source's chip above the stories.
    showOnlySource(event) {
        event.preventDefault()

        const wanted = (event.currentTarget.dataset.feedTitle || '').toLowerCase()
        const chip = Array.from(this.element.querySelectorAll('[data-reader-source-filter]'))
            .find((candidate) => (candidate.dataset.readerSourceFilter || '').toLowerCase() === wanted)
        if (!chip) return

        chip.click()
        chip.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
        chip.focus({ preventScroll: true })
    }

    removeCardSourceFromView(event) {
        event.preventDefault()

        const checkbox = this.selectedFeedCheckbox(event.currentTarget.dataset.feedId)
        if (checkbox) this.commitViewRemoval({ checkbox })
    }

    // On the all-sources model a catalog source is hidden at once, with Undo; a custom feed, the
    // legacy model and guests confirm in the removal dialog first.
    async hideCardSource(event) {
        event.preventDefault()

        const button = event.currentTarget
        const checkbox = this.selectedFeedCheckbox(button.dataset.feedId)
        if (!checkbox) return

        const trigger = button.closest('.nfr-story-source')?.querySelector('.nfr-story-source-button') || button
        const deleteUrl = checkbox.dataset.feedDeleteUrl
        if (button.dataset.instantHide !== 'true' || !deleteUrl || !this.hasUnhideUrlTemplateValue) {
            this.openSourceRemoval({ checkbox, trigger })
            return
        }

        const feedName = button.dataset.feedName || checkbox.dataset.feedName || 'This source'
        try {
            const response = await fetch(deleteUrl, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: { Accept: 'application/json', ...this.csrfHeaders() }
            })
            if (!response.ok) throw new Error(`Hide failed with ${response.status}`)
        } catch (_error) {
            this.showUndoToast(`${feedName} could not be hidden. Try again.`, { undoable: false })
            return
        }

        const row = checkbox.closest('[data-feeds-target="feedPickerRow"]')
        this.lastHiddenSource = { feedId: checkbox.value, feedName, checkbox, row, parent: row?.parentElement }
        checkbox.checked = false
        row?.remove()
        this.updateFeedPickerSourceCount()
        this.updateSelectedFeedSummary(this.feedFormTarget)
        this.requestSelectedFeeds(this.feedFormTarget)
        this.showUndoToast(`${feedName} is hidden everywhere. Find it under Sources › Hidden.`, { undoable: true })
    }

    async undoHide(event) {
        event?.preventDefault()

        const hidden = this.lastHiddenSource
        if (!hidden || !this.hasUnhideUrlTemplateValue) return

        this.lastHiddenSource = null
        const url = this.unhideUrlTemplateValue.replace('__SOURCE_ID__', encodeURIComponent(hidden.feedId))
        try {
            const response = await fetch(url, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: { Accept: 'application/json', ...this.csrfHeaders() }
            })
            if (!response.ok) throw new Error(`Unhide failed with ${response.status}`)
        } catch (_error) {
            this.showUndoToast(`${hidden.feedName} is still hidden. Show it again from Sources › Hidden.`, { undoable: false })
            return
        }

        if (hidden.row) (hidden.parent || this.feedPickerListTarget).append(hidden.row)
        hidden.checkbox.checked = true
        this.updateFeedPickerSourceCount()
        this.updateSelectedFeedSummary(this.feedFormTarget)
        this.requestSelectedFeeds(this.feedFormTarget)
        this.showUndoToast(`${hidden.feedName} is back in your reader.`, { undoable: false })
    }

    showUndoToast(message, { undoable }) {
        if (!this.hasUndoToastTarget) return

        window.clearTimeout(this.undoToastTimeout)
        this.undoToastMessageTarget.textContent = message
        if (this.hasUndoToastButtonTarget) this.undoToastButtonTarget.hidden = !undoable
        this.undoToastTarget.hidden = false
        this.undoToastTimeout = window.setTimeout(() => this.dismissUndoToast(), undoable ? 10000 : 5000)
    }

    dismissUndoToast(event) {
        event?.preventDefault()
        window.clearTimeout(this.undoToastTimeout)
        if (this.hasUndoToastTarget) this.undoToastTarget.hidden = true
    }

    // Search's "Sources named like …" panel: show a hidden source again in place.
    async unhideSource(event) {
        event.preventDefault()

        const button = event.currentTarget
        if (!button.dataset.url || button.disabled) return

        button.disabled = true
        try {
            const response = await fetch(button.dataset.url, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: { Accept: 'application/json', ...this.csrfHeaders() }
            })
            if (!response.ok) throw new Error(`Unhide failed with ${response.status}`)

            button.textContent = button.dataset.doneLabel || 'Shown again'
        } catch (_error) {
            button.disabled = false
            if (typeof toastr !== 'undefined') toastr.error('That source could not be shown again. Try again.')
        }
    }

    selectedFeedCheckbox(feedId) {
        if (!feedId || !this.hasFeedFormTarget) return null

        return this.feedFormTarget.querySelector(`.reader-feed-checkbox[value="${CSS.escape(String(feedId))}"]`)
    }

    csrfHeaders() {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        return csrfToken ? { 'X-CSRF-Token': csrfToken } : {}
    }

    async shareStory(event) {
        event.preventDefault()

        const button = event.currentTarget
        const url = button.dataset.url
        if (!url) return

        const label = button.querySelector('span')
        try {
            await navigator.clipboard.writeText(url)
            if (label) {
                label.textContent = 'Link copied'
                window.setTimeout(() => { label.textContent = 'Share' }, 2000)
            }
            if (typeof toastr !== 'undefined') toastr.success('Link copied to the clipboard')
        } catch (_error) {
            if (typeof toastr !== 'undefined') toastr.error('The link could not be copied')
        }
    }

    async saveStory(event) {
        event.preventDefault()

        const form = event.currentTarget
        const button = form.querySelector('button[type="submit"]')
        if (!button || button.disabled) return

        button.disabled = true
        try {
            const response = await fetch(form.action, {
                method: 'POST',
                body: new FormData(form),
                credentials: 'same-origin',
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...this.csrfHeaders() }
            })
            if (!response.ok) throw new Error(`Save failed with ${response.status}`)

            button.classList.add('is-on')
            button.setAttribute('aria-pressed', 'true')
            button.querySelector('svg')?.setAttribute('fill', 'currentColor')
            const label = button.querySelector('span')
            if (label) label.textContent = button.dataset.savedLabel || 'Saved'
            if (typeof toastr !== 'undefined') toastr.success('Saved. Find it under Saved articles.')
        } catch (_error) {
            button.disabled = false
            if (typeof toastr !== 'undefined') toastr.error('The story could not be saved. Try again.')
        }
    }

    escapeHtml(value) {
        const div = document.createElement('div')
        div.textContent = value
        return div.innerHTML
    }

    applyLocalArticleSearch(value) {
        const terms = (value || '').toLowerCase().trim().split(/\s+/).filter(Boolean)
        const matchesTerms = (element) => {
            if (!terms.length) return true

            const searchableText = [
                element.dataset.title,
                element.dataset.summary,
                element.dataset.feedTitle
            ].filter(Boolean).join(' ')

            return terms.every((term) => searchableText.includes(term))
        }

        document.querySelectorAll('.nfr-source-tabs [data-reader-source-filter]').forEach((chip, index) => {
            chip.classList.toggle('active', index === 0)
        })

        document.querySelectorAll('#feed-items .nfr-article-column[data-title]').forEach((article) => {
            const matches = matchesTerms(article)
            article.classList.toggle('nfr-hidden', !matches)
            article.style.display = matches ? '' : 'none'
        })

        this.updateArticleRowsVisibility()

        let visibleFeaturedSlides = 0
        document.querySelectorAll('.nfr-featured-carousel .swiper-slide[data-title]').forEach((slide) => {
            const matches = matchesTerms(slide)
            slide.classList.toggle('nfr-hidden', !matches)
            slide.style.display = matches ? '' : 'none'
            if (matches) visibleFeaturedSlides += 1
        })

        document.querySelectorAll('.nfr-featured-section').forEach((section) => {
            section.hidden = terms.length > 0 && visibleFeaturedSlides === 0
        })

        document.querySelectorAll('.nfr-featured-carousel .theme-slider').forEach((slider) => {
            if (slider.swiper) slider.swiper.update()
        })
    }

    initializeNewsCards() {
        document.querySelectorAll('.copy-url:not([data-initialized])').forEach((button) => {
            button.dataset.initialized = 'true'
            button.addEventListener('click', (event) => {
                event.preventDefault()
                event.stopPropagation()

                const url = button.dataset.url
                if (!url) return

                navigator.clipboard.writeText(url).then(() => {
                    if (typeof toastr !== 'undefined') {
                        toastr.success('URL copied to clipboard')
                    }
                }).catch(() => {
                    if (typeof toastr !== 'undefined') {
                        toastr.error('Failed to copy URL')
                    }
                })
            })
        })

        document.querySelectorAll('.save-bookmark-form:not([data-initialized])').forEach((form) => {
            form.dataset.initialized = 'true'
            form.addEventListener('submit', (event) => {
                event.preventDefault()

                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
                const formData = new FormData(form)

                fetch(form.action, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'X-CSRF-Token': csrfToken,
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                })
                    .then((response) => {
                        if (!response.ok) throw new Error('Bookmark failed')
                        return response.json()
                    })
                    .then(() => {
                        if (typeof toastr !== 'undefined') {
                            toastr.success('Item saved for later')
                        }
                    })
                    .catch(() => {
                        if (typeof toastr !== 'undefined') {
                            toastr.error('There was an error saving the item')
                        }
                    })
            })
        })

        document.querySelectorAll('.feed-filter-link:not([data-initialized])').forEach((link) => {
            link.dataset.initialized = 'true'
            link.addEventListener('click', (event) => {
                event.preventDefault()
                event.stopPropagation()

                const feedTitle = link.dataset.feedTitle
                const matchingSourceChip = Array.from(document.querySelectorAll('[data-reader-source-filter]'))
                    .find((chip) => chip.dataset.readerSourceFilter === feedTitle)

                if (matchingSourceChip) {
                    matchingSourceChip.click()
                    return
                }

                const searchInput = document.getElementById('article_search') || document.getElementById('global_news_search')
                if (searchInput) {
                    if (searchInput.id === 'article_search') {
                        this.setLocalSearchVisible(true)
                    }

                    searchInput.value = feedTitle
                    searchInput.dispatchEvent(new Event('input', { bubbles: true }))
                    searchInput.focus()
                }
            })
        })

    }

    initializeParallaxImages() {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight
        const cards = Array.from(document.querySelectorAll(LARGE_IMAGE_CARD_SELECTOR))
            .filter((card) => card.querySelector(LARGE_IMAGE_SELECTOR))
        if (!cards.length) return

        cards.forEach((card) => {
            const storedStartTop = Number(card.dataset.nfrParallaxStartTop)
            card.dataset.nfrParallaxBound = 'true'

            if (!Number.isFinite(storedStartTop)) {
                const rect = card.getBoundingClientRect()
                const startTop = scrollPanStartTop({
                    cardTop: rect.top,
                    cardHeight: rect.height,
                    viewportHeight
                })
                card.dataset.nfrParallaxStartTop = String(startTop)
            }
        })

        if (window.newsfeedreaderParallaxBound) {
            this.updateParallaxImages()
            return
        }

        window.newsfeedreaderParallaxBound = true
        window.newsfeedreaderParallaxTicking = false
        window.newsfeedreaderParallaxRebase = false

        const scheduleUpdate = (event) => {
            if (event?.type === 'resize') {
                window.newsfeedreaderParallaxRebase = true
            }
            if (window.newsfeedreaderParallaxTicking) return

            window.newsfeedreaderParallaxTicking = true
            requestAnimationFrame(() => {
                window.newsfeedreaderParallaxTicking = false
                const rebase = window.newsfeedreaderParallaxRebase
                window.newsfeedreaderParallaxRebase = false
                this.updateParallaxImages({ rebase })
            })
        }

        window.addEventListener('scroll', scheduleUpdate, { passive: true })
        window.addEventListener('resize', scheduleUpdate)
        this.updateParallaxImages()
    }

    initializeBackgroundVideos() {
        const videos = document.querySelectorAll(`${BACKGROUND_VIDEO_SELECTOR}:not([data-nfr-video-bound])`)
        if (!videos.length) return

        if (!window.newsfeedreaderBackgroundVideoObserver && 'IntersectionObserver' in window) {
            window.newsfeedreaderBackgroundVideoObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    entry.target.dataset.nfrVideoVisible = String(entry.isIntersecting && entry.intersectionRatio >= 0.25)
                    updateBackgroundVideoPlayback(entry.target)
                })
            }, { threshold: [0, 0.25] })
        }

        if (!window.newsfeedreaderBackgroundVideoEventsBound) {
            window.newsfeedreaderBackgroundVideoEventsBound = true
            window.newsfeedreaderBackgroundVideoMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
            if (window.newsfeedreaderBackgroundVideoMotionQuery.addEventListener) {
                window.newsfeedreaderBackgroundVideoMotionQuery.addEventListener('change', updateBackgroundVideoCollection)
            } else {
                window.newsfeedreaderBackgroundVideoMotionQuery.addListener(updateBackgroundVideoCollection)
            }
            document.addEventListener('visibilitychange', updateBackgroundVideoCollection)
        }

        videos.forEach((video) => {
            video.dataset.nfrVideoBound = 'true'
            video.dataset.nfrVideoVisible = 'false'
            video.muted = true
            video.addEventListener('playing', () => video.classList.add('is-playing'))
            video.addEventListener('error', () => {
                video.classList.remove('is-playing')
                video.classList.add('is-unavailable')
            })
            window.newsfeedreaderBackgroundVideoObserver?.observe(video)
        })
    }

    updateParallaxImages({ rebase = false } = {}) {
        const cards = document.querySelectorAll('[data-nfr-parallax-bound]')

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            cards.forEach((card) => card.style.removeProperty(SCROLL_PAN_PROPERTY))
            return
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight

        cards.forEach((card) => {
            const rect = card.getBoundingClientRect()
            const storedStartTop = Number(card.dataset.nfrParallaxStartTop)
            const currentPosition = Number.parseFloat(card.style.getPropertyValue(SCROLL_PAN_PROPERTY)) / 100
            const startTop = rebase && rect.top < viewportHeight
                ? scrollPanRebasedStartTop({
                    cardTop: rect.top,
                    cardHeight: rect.height,
                    viewportHeight,
                    progress: currentPosition
                })
                : Number.isFinite(storedStartTop)
                    ? Math.min(storedStartTop, viewportHeight)
                    : scrollPanStartTop({
                        cardTop: rect.top,
                        cardHeight: rect.height,
                        viewportHeight
                    })
            const positionValue = scrollPanPositionValue({
                startTop,
                cardTop: rect.top,
                cardHeight: rect.height
            })

            card.dataset.nfrParallaxStartTop = String(startTop)

            if (card.style.getPropertyValue(SCROLL_PAN_PROPERTY).trim() !== positionValue) {
                card.style.setProperty(SCROLL_PAN_PROPERTY, positionValue)
            }
        })
    }

    disconnect() {
        if (this.subscription) {
            this.subscription.unsubscribe()
        }

        if (this.boundRefreshReaderContent) {
            document.removeEventListener('turbo:load', this.boundRefreshReaderContent)
        }

        if (this.boundFrameLoad) {
            document.removeEventListener('turbo:frame-load', this.boundFrameLoad)
        }

        if (this.boundFeedPickerKeydown) {
            document.removeEventListener('keydown', this.boundFeedPickerKeydown)
        }

        if (this.boundFontsLoaded) {
            document.fonts?.removeEventListener?.('loadingdone', this.boundFontsLoaded)
        }

        this.expandToggleObserver?.disconnect()
        this.expandToggleObserver = null

        if (this.beforeStreamRenderHandler) {
            document.removeEventListener('turbo:before-stream-render', this.beforeStreamRenderHandler)
        }

        if (this.turboRenderHandler) {
            document.removeEventListener('turbo:render', this.turboRenderHandler)
        }

        window.clearTimeout(this.loadingTimeout)
        window.clearTimeout(this.localSearchAnimationTimeout)
        window.clearTimeout(this.feedPickerCloseTimeout)
        window.clearTimeout(this.feedPickerSearchTimeout)
        window.clearTimeout(this.undoToastTimeout)
        this.feedPickerSearchController?.abort()
        if (this.hasSourceRemovalDialogTarget && this.sourceRemovalDialogTarget.open) {
            this.sourceRemovalDialogTarget.close()
        }
        document.body.classList.remove('nfr-reader-sheet-open')
    }

    setupStreamHandlers() {
        if (this.streamHandlersBound) return

        this.beforeStreamRenderHandler = (event) => {
            const originalRender = event.detail.render
            event.detail.render = async (streamElement) => {
                await originalRender(streamElement)
                requestAnimationFrame(() => this.refreshReaderContent())
            }

            if (event.target.action === 'append') {
                this.handleStreamData(event)
            }
        }

        this.turboRenderHandler = () => {
            this.initializeToasts()
            this.refreshReaderContent()
        }

        document.addEventListener('turbo:before-stream-render', this.beforeStreamRenderHandler)
        document.addEventListener('turbo:render', this.turboRenderHandler)
        this.streamHandlersBound = true
    }

    handleStreamData(event) {
        if (event.target && this.hasContentTarget) {
            const content = event.target.templateContent
            if (content) {
                this.contentTarget.appendChild(content.cloneNode(true))
                this.initializeToasts()
                this.formatLocalTimes()
            }
        }
    }

    initializeToasts() {
        const toastElList = document.querySelectorAll('.nfr-toast:not([data-nfr-toast-initialized])')
        toastElList.forEach(toastEl => {
            toastEl.dataset.nfrToastInitialized = 'true'
            toastEl.hidden = false
            requestAnimationFrame(() => toastEl.classList.add('is-visible'))

            const closeToast = () => {
                toastEl.classList.remove('is-visible')
                window.setTimeout(() => toastEl.remove(), 180)
            }

            toastEl.querySelector('[data-nfr-toast-close]')?.addEventListener('click', closeToast, { once: true })
            window.setTimeout(closeToast, 3000)
        })
    }
}
